import { useEffect, useRef, useState } from 'react';
import Autolinker from 'autolinker';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import type { ChatAdapter, ChatMessage } from '@azure/communication-react';
import { ChatComposite, COMPOSITE_LOCALE_EN_US, COMPOSITE_LOCALE_FR_FR } from '@azure/communication-react';
import { IonCol, IonContent, IonGrid, IonRow } from '@ionic/react';

import './Chat.component.scss';

import { avatarPersonaData, getCustomTheme } from '../../helpers/chat.helper';
import { GuestWaiting } from '../advocate/GuestWaiting';

export const Chat = ({
  chatAdapter,
  forServiceProviders,
  showGuestWaiting,
}: {
  chatAdapter: ChatAdapter;
  forServiceProviders: boolean;
  showGuestWaiting?: boolean;
}) => {
  const { i18n } = useTranslation();
  const contentRef = useRef<HTMLIonContentElement>(null);
  const guestWaitingRef = useRef<HTMLIonRowElement>(null);
  const [chatContentHeight, setChatContentHeight] = useState(0);
  const [guestWaitingContentHeight, setGuestWaitingChatContentHeight] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setChatContentHeight(contentRef.current?.clientHeight || 0);
      setGuestWaitingChatContentHeight(guestWaitingRef.current?.offsetHeight || 0); // offsetHeight to include the padding
    });
    // FIXME
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
    resizeObserver.observe(contentRef.current);
    if (!guestWaitingRef.current) return;
    // FIXME
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
    resizeObserver.observe(guestWaitingRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <IonContent ref={contentRef} fullscreen scrollY={false}>
      <IonGrid
        className={`ion-no-padding ph-no-capture ${forServiceProviders ? 'ts-light-blue-bg' : 'ts-sand-brown-bg'}`}
      >
        {showGuestWaiting && <GuestWaiting ref={guestWaitingRef} />}
        <IonRow className="ion-justify-content-center ">
          <IonCol className="chat-col" style={{ height: chatContentHeight - guestWaitingContentHeight }}>
            <ChatComposite
              fluentTheme={getCustomTheme(forServiceProviders)}
              onFetchAvatarPersonaData={() => Promise.resolve(avatarPersonaData)}
              options={{ topic: false }}
              adapter={chatAdapter}
              locale={i18n.language === 'fr' ? COMPOSITE_LOCALE_FR_FR : COMPOSITE_LOCALE_EN_US}
              onRenderMessage={(messageProps, defaultOnRender) => {
                const message = messageProps.message as ChatMessage;
                if (message.content) {
                  message.content = DOMPurify.sanitize(Autolinker.link(message.content));
                  message.contentType = 'html';
                  messageProps.message = message;
                }
                return defaultOnRender ? defaultOnRender(messageProps) : <></>;
              }}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonContent>
  );
};
